import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { AbstractTableViewProvider } from "../../common/tables/view/AbstractTableViewProvider";
import { Pane } from "evergreen-ui";
import { DefaultStoreTableConfig } from "./tables/config/DefaultStoreTableConfig";
import {
    queryStoresForAdmin,
    useCreateStoreMutation,
    useUpdateStoreMutation
} from "../../datastore/StoreDataStore";
import { Store } from "../../../../common/models";
import { StoreForAdmin } from "../../../../common/tables/store/StoreForAdmin";
import { TableCellFactory } from "../../common/tables/factory";
import { StoreTableCellFactory } from "./tables/factory/StoreTableCellFactory";
import { TablePageHeader } from "../../common/tables/view/TablePageHeader";
import { TableView } from "../../common/tables/view/TableView";
import { StoreSearchInput } from "../Store/StoreSearchInput";
import { generatePath, useHistory } from "react-router-dom";
import { NEW_STORE_ROUTE_PATH } from "../../navigation/Routes";
import { StoreTabIds } from "../Store/MultiTabNewStorePage";

const storeTableCellFactory: TableCellFactory<StoreForAdmin, Store> = new StoreTableCellFactory();

export const AdminStoreTableView: FC = () => {
    const history = useHistory();

    const [
        updateStore,
        {
            loading,
            error,
            called,
        },
    ] = useUpdateStoreMutation();

    const onUpdateStore = useCallback((
        prevData: Store[],
        id: string | number,
        columnName: keyof StoreForAdmin,
        value: string | number | boolean | null,
    ): Store[] => {
        return prevData.map((original: Store) => {
            // Skip over all Store rows that aren't being updated.
            if (id !== original.id) {
                return original;
            }

            // Modify the Store object with the new cell value.
            const storeToUpdate: Store = {
                ...original,
                [columnName]: value
            };

            // Update the server with the modified cell value.
            updateStore(storeToUpdate);

            // Update the React state with the modified cell value.
            return storeToUpdate;
        });
    }, [updateStore]);

    const emptyViewProps = useMemo(() => {
        return {
            primaryText: "No stores found",
            secondaryText: "Add Store",
            onClickSecondaryText: () => history.push(
                generatePath(NEW_STORE_ROUTE_PATH, {
                    tabId: StoreTabIds.details,
                }),
            ),
        };
    }, []);

    const errorViewProps = useMemo(() => {
        return {
            primaryText: "Failed to load stores",
            secondaryText: "Refresh the page to try again",
        };
    }, []);

    return (
        <Pane height={"100%"} display={"flex"} flexDirection={"column"}>
            <TablePageHeader
                title={"Stores"}
                addButtonText={"Add Store"}
                addButtonOnClick={() => history.push(
                    generatePath(NEW_STORE_ROUTE_PATH, {
                        tabId: StoreTabIds.details,
                    }),
                )}
                searchBar={(
                    <StoreSearchInput
                        placeholderText={"Search for store"}
                        textInputProps={{ fontSize: 14, height: 40, paddingLeft: 36, borderRadius: 8, width: 200 }}
                        leftIconContainerProps={{ width: 36 }}
                        onSelectSearchResult={(label: string, value: string) => {
                            window.open(`/stores/${value}`, "_blank");
                        }}
                    />
                )}
            />
            <AbstractTableViewProvider
                queryTableData={queryStoresForAdmin}
                onUpdateRow={onUpdateStore}
                updateRowLoading={loading}
                updateRowCalled={called}
                updateRowError={error}
                tableCellFactory={storeTableCellFactory}
                savedTableConfigProps={{
                    tableName: "storesForAdmin",
                    identifiers: ["v4"],
                    defaultConfigValue: DefaultStoreTableConfig,
                }}
            >
                <TableView
                    emptyViewProps={emptyViewProps}
                    errorViewProps={errorViewProps}
                />
            </AbstractTableViewProvider>
        </Pane>
    );
};
